import {
  ApiError,
  ApiTimeoutError,
  ApiUnreachableError,
  isTwoFactorEnrollmentRequiredError,
} from './api';

/** A 401 is the one answer that means "this session is not authenticated". */
export function isAuthenticationFailure(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

const RETRYABLE_CLIENT_STATUSES: readonly number[] = [408, 425, 429];

export function isTransientFailure(error: unknown): boolean {
  if (error instanceof ApiUnreachableError || error instanceof ApiTimeoutError) return true;
  if (error instanceof ApiError) {
    return error.status >= 500 || RETRYABLE_CLIENT_STATUSES.includes(error.status);
  }
  return false;
}

/** What can actually get the user past this failure. */
export type NonRetryableFailureKind = 'authorization' | 'unavailable';

/** Non-null when pressing "Try again" can only reproduce the same failure. */
export function nonRetryableFailureKind(error: unknown): NonRetryableFailureKind | null {
  if (!(error instanceof ApiError)) return null;
  const { status } = error;
  if (status < 400 || status >= 500) return null;
  if (status === 401) return null;
  if (RETRYABLE_CLIENT_STATUSES.includes(status)) return null;
  if (isTwoFactorEnrollmentRequiredError(error)) return null;
  return status === 403 ? 'authorization' : 'unavailable';
}

/** True when a retry cannot change the answer. */
export function isNonRetryableFailure(error: unknown): boolean {
  return nonRetryableFailureKind(error) !== null;
}

/** Backoff for the silent retries. */
export const SESSION_RETRY_DELAYS_MS = [600, 1800, 4000];

export function isUnreachableFailure(error: unknown): boolean {
  return error instanceof ApiUnreachableError || error instanceof ApiTimeoutError;
}
